
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

/**
 * CanBusStatus Component
 * 
 * Displays the CAN bus connection status and related information.
 * Shows one of three states: controlled, connected, or direct sensor readings.
 * Also handles the "no data" state when regulator needs configuration.
 */
function CanBusStatus({ status, connectionMode, translatedChargeStage, isVisible, isNoDataDetected = false }) {
  if (!isVisible) {
    return null
  }

  // Handle no data state (matches original HTML dashboard behavior)
  if (isNoDataDetected) {
    return (
      <div className="card shadow-sm border-0 mb-3" style={{ 
        background: 'linear-gradient(to right, #ffffff, #f8f9fa)',
        transition: 'all 0.3s ease',
      }}>
        <div className="card-header fw-bold border-0">
          <i className="bi bi-exclamation-circle-fill text-danger fs-4 me-2" style={{ color: '#ff0000 !important' }} aria-hidden="true"></i>
          <span className="canbus-connected-text">No data detected</span>
        </div>
        <div className="card-body" style={{ paddingBottom: '0.75rem' }}>
          <div style={{ marginBottom: '-0.5rem' }}>
            <p className="mb-2">Configure the regulator to start monitoring.</p>
            <Link 
              to="/configuration" 
              className="btn btn-primary btn-sm"
              style={{ 
                transition: 'all 0.2s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)'
                e.target.style.boxShadow = '0 4px 8px rgba(0,123,255,0.2)'
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)'
                e.target.style.boxShadow = 'none'
              }}
            >
              <i className="bi bi-gear me-1"></i>
              Go to Configuration
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Determine header content based on connection mode
  const getHeaderContent = () => {
    switch (connectionMode) {
      case 'controlled':
        return (
          <>
            <i className="bi bi-battery-charging fs-4 me-2" aria-hidden="true"></i>
            <span className="canbus-connected-text">CAN bus controlled charging</span>
          </>
        )
      case 'connected':
        return (
          <>
            <i className="bi bi-check-circle fs-4 me-2" aria-hidden="true"></i>
            <span className="canbus-connected-text">CAN bus connected</span>
          </>
        )
      default:
        return (
          <>
            <i className="bi bi-speedometer2 fs-4 me-2" aria-hidden="true"></i>
            <span className="canbus-connected-text">Operating with direct sensor readings</span>
          </>
        )
    }
  }

  // Build body content based on available data
  const buildBodyContent = () => {
    const details = []

    // Charge stage (available in all modes, but not during cutoff)
    if (translatedChargeStage && !status.cutoffSignal) {
      details.push(
        <p key="charge-stage" className="card-text mb-2">
          Charge Stage: {translatedChargeStage}
        </p>,
      )
    }

    // Target values (only in controlled mode, and not during cutoff)
    if (connectionMode === 'controlled' && !status.cutoffSignal) {
      if (status.targetVolts != null) {
        details.push(
          <p key="target-volts" className="card-text mb-2">
            Battery Target Volts: {status.targetVolts.toFixed(1)}V{' '}
            <i 
              className="bi bi-info-circle" 
              title="Target voltage requested by the battery management system via CAN bus"
              aria-label="Information about target voltage"
            ></i>
          </p>,
        )
      }

      if (status.targetAmps != null) {
        details.push(
          <p key="target-amps" className="card-text mb-2">
            Battery Target Amps: {Math.round(status.targetAmps)}A{' '}
            <i 
              className="bi bi-info-circle" 
              title="Maximum current requested by the battery management system via CAN bus"
              aria-label="Information about target current"
            ></i>
          </p>,
        )
      }
    }

    // Alternator target amps (controlled and connected modes)
    if (connectionMode !== 'direct') {
      if (status.alt1TargetAmps != null) {
        details.push(
          <p key="alt1-target-amps" className="card-text mb-2">
            Alternator 1 Max Amps: {Math.round(status.alt1TargetAmps)}A{' '}
            <i 
              className="bi bi-info-circle" 
              title="Maximum current requested by the battery management system for this alternator"
              aria-label="Information about alternator 1 max current"
            ></i>
          </p>,
        )
      }

      if (status.alt2TargetAmps != null) {
        details.push(
          <p key="alt2-target-amps" className="card-text mb-2">
            Alternator 2 Max Amps: {Math.round(status.alt2TargetAmps)}A{' '}
            <i 
              className="bi bi-info-circle" 
              title="Maximum current requested by the battery management system for this alternator"
              aria-label="Information about alternator 2 max current"
            ></i>
          </p>,
        )
      }
    }

    // Boost mode indicators (all modes)
    if (status.alt1BoostEngaged) {
      const boostColor = connectionMode === 'controlled' ? '#ff0000' : ''
      const boostClass = connectionMode === 'controlled' ? '' : 'text-primary'
      
      details.push(
        <p key="alt1-boost" className={`card-text mb-2 ${boostClass}`}>
          <i 
            className="bi bi-lightning-charge-fill me-1" 
            style={boostColor ? { color: boostColor } : {}}
            aria-hidden="true"
          ></i>
          <strong>Alternator 1 Boost Mode {connectionMode === 'controlled' ? 'active' : 'Active'}</strong>
        </p>,
      )
    }

    if (status.alt2BoostEngaged) {
      const boostColor = connectionMode === 'controlled' ? '#ff0000' : ''
      const boostClass = connectionMode === 'controlled' ? '' : 'text-primary'
      
      details.push(
        <p key="alt2-boost" className={`card-text mb-2 ${boostClass}`}>
          <i 
            className="bi bi-lightning-charge-fill me-1" 
            style={boostColor ? { color: boostColor } : {}}
            aria-hidden="true"
          ></i>
          <strong>Alternator 2 Boost Mode {connectionMode === 'controlled' ? 'active' : 'Active'}</strong>
        </p>,
      )
    }

    // Charge cutoff signal (all modes)
    if (status.cutoffSignal) {
      // Check if global RV-C cutoff is present
      const hasGlobalCutoff = status.cutoffBatteries?.some(batNum => 
        status.cutoffReasons?.[batNum]?.includes('Charging disabled by RV-C command'),
      )
      
      // Show global cutoff separately if present
      if (hasGlobalCutoff) {
        details.push(
          <p key="cutoff-global" className="card-text mb-2 text-danger">
            <i className="bi bi-exclamation-octagon me-1" aria-hidden="true"></i>
            <strong>Charging disabled by RV-C command</strong>
          </p>,
        )
      }
      
      // Battery-specific cutoff logic - filter out global reason since we show it separately
      const batteryDetails = status.cutoffBatteries?.map(batNum => {
        const batteryName = `Battery ${batNum}`
        const reasons = status.cutoffReasons?.[batNum]?.filter(r => 
          r !== 'Charging disabled by RV-C command',
        )
        if (reasons?.length) {
          return `${batteryName} (${reasons.join(', ')})`
        }
        return batteryName
      }).join(' and ') || 'Battery'
      
      // Only show battery message if there are non-global reasons or if no global cutoff
      if (!hasGlobalCutoff || batteryDetails !== 'Battery 1') {
        details.push(
          <p key="cutoff-signal" className="card-text mb-2 text-danger">
            <i className="bi bi-exclamation-octagon me-1" aria-hidden="true"></i>
            <strong>Receiving charge cutoff signal from {batteryDetails}</strong>
          </p>,
        )
      }
    }

    // Ignition off warning
    if (status.ignitionOff) {
      const alternatorText = status.ignitionOffAlternators?.length > 0 
        ? status.ignitionOffAlternators.length === 1 
          ? `Alternator ${status.ignitionOffAlternators[0]}`
          : `Alternators ${status.ignitionOffAlternators.join(' and ')}`
        : 'Alternator'
      
      details.push(
        <p key="ignition-off" className="card-text mb-2 text-warning">
          <i className="bi bi-key-fill me-1" aria-hidden="true"></i>
          <strong>Charging paused - {alternatorText}: Ignition off</strong>
        </p>,
      )
    }

    // Engine off (RPM = 0) warning
    if (status.engineOff) {
      const alternatorText = status.engineOffAlternators?.length > 0 
        ? status.engineOffAlternators.length === 1 
          ? `Alternator ${status.engineOffAlternators[0]}`
          : `Alternators ${status.engineOffAlternators.join(' and ')}`
        : 'Alternator'
      
      details.push(
        <p key="engine-off" className="card-text mb-2 text-primary">
          <i className="bi bi-pause-circle-fill me-1" aria-hidden="true"></i>
          <strong>Charging paused - {alternatorText}: No RPM detected</strong>
        </p>,
      )
    }

    return details
  }

  return (
    <div
      className="card shadow-sm border-0 mb-3"
      style={{
        display: 'block',
        background: 'linear-gradient(to right, #ffffff, #f8f9fa)',
        transition: 'all 0.3s ease',
      }}
      data-testid="canbus-status"
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)'
        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'
      }}
    >
      <div className="card-header fw-bold border-0">
        {getHeaderContent()}
      </div>
      <div className="card-body" id="canbus-status-body" style={{ paddingBottom: '0.75rem' }}>
        <div style={{ marginBottom: '-0.5rem' }}>
          {buildBodyContent()}
        </div>
      </div>
    </div>
  )
}

CanBusStatus.propTypes = {
  /** CAN bus status object containing connection data */
  status: PropTypes.shape({
    targetVolts: PropTypes.number,
    targetAmps: PropTypes.number,
    alt1TargetAmps: PropTypes.number,
    alt2TargetAmps: PropTypes.number,
    alt1BoostEngaged: PropTypes.bool,
    alt2BoostEngaged: PropTypes.bool,
    cutoffSignal: PropTypes.bool,
    cutoffBatteries: PropTypes.arrayOf(PropTypes.number),
    cutoffReasons: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    ignitionOff: PropTypes.bool,
    ignitionOffAlternators: PropTypes.arrayOf(PropTypes.number),
    engineOff: PropTypes.bool,
    engineOffAlternators: PropTypes.arrayOf(PropTypes.number),
  }),
  /** Connection mode: 'controlled', 'connected', or 'direct' */
  connectionMode: PropTypes.oneOf(['controlled', 'connected', 'direct']),
  /** Translated charge stage display text */
  translatedChargeStage: PropTypes.string,
  /** Controls visibility of the status card */
  isVisible: PropTypes.bool,
  /** Whether to show no data detected message */
  isNoDataDetected: PropTypes.bool,
}

CanBusStatus.defaultProps = {
  status: {},
  connectionMode: 'direct',
  translatedChargeStage: null,
  isVisible: true,
  isNoDataDetected: false,
}

export default CanBusStatus
